<template>
    <div class="queue-filter-chips">
        <div class="d-flex flex-wrap align-items-center chip-row">
            <div v-for="filter in visibleFilters" :key="filter.key" class="chip-wrapper">
                <div :id="chipDomId(filter.key)" class="chip"
                    :class="{ 'chip-active': filter.isActive(), 'chip-editing': editingKey === filter.key, 'chip-toggle': isToggle(filter) }">
                    <button type="button" class="chip-body" @click="handleChipClick(filter)">
                        <i v-if="isToggle(filter)" class="chip-toggle-indicator fa"
                            :class="filter.isActive() ? 'fa-check-square' : 'fa-square-o'"></i>
                        <span class="chip-label">{{ filter.label }}</span>
                        <template v-if="!isToggle(filter)">
                            <span v-if="filter.isActive()" class="chip-value"
                                :style="valueStyle(filter)">{{ filter.summary() }}</span>
                            <span v-else class="chip-value chip-empty"
                                :style="valueStyle(filter)">Any</span>
                        </template>
                    </button>
                    <button v-if="!isPermanent(filter)" type="button" class="chip-clear"
                        aria-label="Remove filter" @click.stop="removeFilter(filter)">
                        <i class="fa fa-times"></i>
                    </button>
                    <button v-else-if="filter.isActive() && !isToggle(filter)" type="button" class="chip-clear"
                        aria-label="Clear filter" @click.stop="clearFilter(filter)">
                        <i class="fa fa-times"></i>
                    </button>
                </div>

                <b-popover v-if="!isToggle(filter)" :target="chipDomId(filter.key)"
                    :show="editingKey === filter.key" triggers="manual" placement="bottom"
                    custom-class="chip-popover" :no-fade="false">
                    <div :ref="popoverRefName(filter.key)" class="chip-popover-body"
                        :style="popoverBodyStyle(filter.key)">
                        <div class="chip-popover-header-extra">
                            <slot name="header-extra" :filter="filter" />
                        </div>
                        <slot name="editor" :filter="filter" :close="closeEditor" />
                    </div>
                </b-popover>
            </div>

            <b-dropdown v-if="addableFilters.length" size="sm" variant="link" class="add-filter-dropdown"
                no-caret toggle-class="add-filter-toggle">
                <template #button-content>
                    <i class="fa fa-plus"></i> Filter
                </template>
                <b-dropdown-item v-for="filter in addableFilters" :key="filter.key"
                    @click="openEditor(filter.key)">
                    {{ filter.label }}
                </b-dropdown-item>
            </b-dropdown>

            <b-button v-if="anyActive" variant="link" size="sm" class="clear-all-btn"
                @click="clearAll">
                <i class="fa fa-times-circle"></i> Clear all
            </b-button>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';

export interface FilterDef {
    key: string;
    label: string;
    isActive: () => boolean;
    summary: () => string;
    clear: () => void;
    /**
     * If provided, the chip behaves as a switch: clicking the chip body invokes toggle()
     * directly instead of opening an editor popover. The chip's active styling reflects
     * isActive(); no value/Any text is rendered.
     */
    toggle?: () => void;
    /**
     * Fixed pixel width for the value text slot. Useful for list-style filters
     * (statuses, programs) where the summary can grow as the user picks more items.
     * Without this, the chip resizes on every pick and shifts neighboring chips.
     */
    valueWidth?: number;
}

// Class fragments on teleported popups that we should NOT treat as "outside" clicks.
const TELEPORTED_POPUP_SELECTORS = [
    '.multiselect__content-wrapper',
    '.el-picker-panel',
    '.el-time-panel',
    '.dropdown-menu',
    '.b-popover',
    '.popover',
];

// Inner dropdown/overlay popups (multiselect, date-picker, bootstrap dropdown) that a
// user's Escape should collapse before we close the whole editor. Excludes the popover
// itself, which is always showing while an editor is open.
const INNER_OVERLAY_SELECTORS = [
    '.multiselect__content-wrapper',
    '.el-picker-panel',
    '.el-time-panel',
    '.dropdown-menu',
];

// True when any inner overlay is currently visible. Checked synchronously during Escape's
// keydown: Vue flushes DOM updates on the next tick, so an inner control that just closed
// its own dropdown still reads as visible here — which is what gives Escape its two-stage
// behavior (first press collapses the inner dropdown, second closes the editor).
function hasOpenInnerOverlay(): boolean {
    return INNER_OVERLAY_SELECTORS.some(sel =>
        Array.from(document.querySelectorAll<HTMLElement>(sel))
            .some(el => el.getClientRects().length > 0)
    );
}

let chipIdSeq = 0;

@Component({ name: 'QueueFilterChips' })
export default class QueueFilterChips extends Vue {
    @Prop({ required: true }) readonly filters!: FilterDef[];
    @Prop({ default: () => [] }) readonly pinnedKeys!: string[];
    // Non-default filters the user has added via "+ Filter". Owned + persisted by the
    // parent; the component emits filter-revealed / filter-hidden to keep it in sync.
    @Prop({ default: () => [] }) readonly revealedKeys!: string[];

    private editingKey: string | null = null;
    private editingChipWidth: number | null = null;
    private temporarilyShown: Set<string> = new Set();
    private readonly instanceId = ++chipIdSeq;

    // Popover body content width must equal chip width minus popover padding/borders.
    private static readonly POPOVER_HORIZONTAL_CHROME = 18;
    // Minimum popover width so editors (search inputs, multiselects) don't get clipped
    // when the chip itself is narrow (e.g. "Store Any"). Wide enough that a multiselect
    // option label and its "Press enter to select" hint sit side by side without colliding.
    private static readonly POPOVER_MIN_WIDTH = 320;

    get visibleFilters(): FilterDef[] {
        return this.filters.filter(f =>
            this.pinnedKeys.includes(f.key) ||
            this.revealedKeys.includes(f.key) ||
            f.isActive() ||
            this.temporarilyShown.has(f.key)
        );
    }

    get addableFilters(): FilterDef[] {
        return this.filters.filter(f =>
            !this.pinnedKeys.includes(f.key) &&
            !this.revealedKeys.includes(f.key) &&
            !f.isActive() &&
            !this.temporarilyShown.has(f.key)
        );
    }

    get anyActive(): boolean {
        return this.filters.some(f => f.isActive());
    }

    chipDomId(key: string): string {
        return `queue-filter-chip-${this.instanceId}-${key}`;
    }

    popoverRefName(key: string): string {
        return `popover-${key}`;
    }

    isToggle(filter: FilterDef): boolean {
        return typeof filter.toggle === 'function';
    }

    // Default (always-shown) filters can have their value cleared but not removed from
    // the bar. Non-permanent filters were added via "+ Filter" and get a remove control.
    isPermanent(filter: FilterDef): boolean {
        return this.pinnedKeys.includes(filter.key);
    }

    valueStyle(filter: FilterDef): Record<string, string> | null {
        return filter.valueWidth
            ? { width: `${filter.valueWidth}px`, flex: '0 0 auto' }
            : null;
    }

    popoverBodyStyle(key: string): Record<string, string> | null {
        if (this.editingKey !== key || this.editingChipWidth == null) return null;
        const chipInner = Math.max(0, this.editingChipWidth - QueueFilterChips.POPOVER_HORIZONTAL_CHROME);
        const w = Math.max(chipInner, QueueFilterChips.POPOVER_MIN_WIDTH);
        return { width: `${w}px` };
    }

    handleChipClick(filter: FilterDef) {
        if (this.isToggle(filter)) {
            filter.toggle!();
            return;
        }
        this.toggleEditor(filter.key);
    }

    toggleEditor(key: string) {
        this.editingKey = this.editingKey === key ? null : key;
    }

    openEditor(key: string) {
        const filter = this.filters.find(f => f.key === key);
        const next = new Set(this.temporarilyShown);
        next.add(key);
        this.temporarilyShown = next;
        // Persist the reveal so the added filter stays in the bar across sessions.
        this.$emit('filter-revealed', key);
        // Toggle filters have no editor popover; revealing the chip is enough.
        if (filter && !this.isToggle(filter)) this.editingKey = key;
    }

    closeEditor() {
        // Revealed filters persist until explicitly removed via their "x"; closing the
        // editor only dismisses the popover.
        this.editingKey = null;
    }

    // Clears the value of a permanent (default) filter without removing its chip.
    clearFilter(filter: FilterDef) {
        filter.clear();
        if (this.editingKey === filter.key) this.editingKey = null;
    }

    // Removes an added (non-default) filter from the bar back to "+ Filter" and clears
    // its value. The parent drops it from revealedKeys so the removal persists.
    removeFilter(filter: FilterDef) {
        filter.clear();
        if (this.temporarilyShown.has(filter.key)) {
            const next = new Set(this.temporarilyShown);
            next.delete(filter.key);
            this.temporarilyShown = next;
        }
        if (this.editingKey === filter.key) this.editingKey = null;
        this.$emit('filter-hidden', filter.key);
    }

    clearAll() {
        for (const f of this.filters) {
            if (f.isActive()) f.clear();
        }
        this.temporarilyShown = new Set();
        this.editingKey = null;
    }

    @Watch('editingKey')
    onEditingKeyChange(newKey: string | null) {
        if (newKey) {
            this.measureChipWidth(newKey);
            this.$nextTick(() => this.autoFocusEditor(newKey));
        } else {
            this.editingChipWidth = null;
        }
    }

    private measureChipWidth(key: string) {
        const el = document.getElementById(this.chipDomId(key));
        this.editingChipWidth = el ? Math.round(el.getBoundingClientRect().width) : null;
    }

    private autoFocusEditor(key: string) {
        const refs = this.$refs[this.popoverRefName(key)] as HTMLElement[] | HTMLElement | undefined;
        const root = Array.isArray(refs) ? refs[0] : refs;
        if (!root) return;
        // A vue-multiselect editor should open its option list as soon as the chip opens,
        // matching the v-select editors (which auto-open via :dropdown-open). Activate it
        // directly instead of focusing the wrapper — focusing alone only shows an empty box,
        // and the first focusable element can be a sibling control (e.g. the Statuses
        // "Show all" toggle) that renders before the multiselect.
        const multiselect = root.querySelector<HTMLElement>('.multiselect');
        const multiselectVm = multiselect && (multiselect as unknown as { __vue__?: { activate?: () => void } }).__vue__;
        if (multiselectVm && typeof multiselectVm.activate === 'function') {
            multiselectVm.activate();
            return;
        }
        const candidate = root.querySelector<HTMLElement>(
            'input:not([type=hidden]), .multiselect, .el-input__inner, button:not(.popover-actions button), [tabindex]'
        );
        if (candidate && typeof candidate.focus === 'function') {
            candidate.focus();
        }
    }

    // Bound once in mounted() and cached in boundDocClick/boundKeydown so that
    // addEventListener and removeEventListener operate on the same function reference.
    // `this` is the component instance, so this.editingKey reads the live reactive
    // value regardless of how the handler is bound.
    onDocClick(e: MouseEvent) {
        if (!this.editingKey) return;
        const target = e.target as HTMLElement;
        if (!target) return;
        // Click inside any chip wrapper (own toggle / clear)? Let the click handlers manage it.
        if (target.closest('.chip-wrapper')) return;
        // Click inside the popover body itself? Stay open.
        if (target.closest('.chip-popover')) return;
        // Click inside a teleported popup (multiselect dropdown, el-date-picker panel, etc)? Stay open.
        for (const sel of TELEPORTED_POPUP_SELECTORS) {
            if (target.closest(sel)) return;
        }
        this.closeEditor();
    }

    onKeydown(e: KeyboardEvent) {
        if (e.key !== 'Escape' || !this.editingKey) return;
        // Let an open inner dropdown (multiselect, date-picker, etc.) consume Escape
        // first; only tear down the whole editor once no inner overlay is showing.
        if (hasOpenInnerOverlay()) return;
        this.closeEditor();
    }

    private boundDocClick?: (e: MouseEvent) => void;
    private boundKeydown?: (e: KeyboardEvent) => void;

    mounted() {
        // Bind once and store the reference so the same function is used for add + remove.
        this.boundDocClick = this.onDocClick.bind(this);
        this.boundKeydown = this.onKeydown.bind(this);
        // Capture-phase mousedown so outside-detection keys off where the press STARTED:
        // a drag that begins inside the popover (e.g. selecting text) and releases just
        // outside must not close the editor. A bubble-phase 'click' listener would, because
        // the browser dispatches that click to the common ancestor of mousedown/mouseup.
        document.addEventListener('mousedown', this.boundDocClick, true);
        window.addEventListener('keydown', this.boundKeydown);
    }

    beforeDestroy() {
        if (this.boundDocClick) {
            document.removeEventListener('mousedown', this.boundDocClick, true);
        }
        if (this.boundKeydown) {
            window.removeEventListener('keydown', this.boundKeydown);
        }
    }
}
</script>

<style scoped>
.chip-row {
    gap: 6px;
    row-gap: 8px;
}

.chip-wrapper {
    display: inline-flex;
}

.chip {
    display: inline-flex;
    align-items: stretch;
    border: 1px solid #d6d8db;
    border-radius: 16px;
    background: #fff;
    font-size: 0.85rem;
    line-height: 1;
    overflow: hidden;
    transition: border-color 0.15s, box-shadow 0.15s;
}

.chip:hover {
    border-color: #9ea3a8;
}

.chip-active {
    border-color: #1779ba;
    background: #eaf3fb;
}

.chip-editing {
    border-color: #1779ba;
    box-shadow: 0 0 0 2px rgba(23, 121, 186, 0.15);
}

.chip-body {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    padding: 6px 12px;
    border: none;
    background: transparent;
    cursor: pointer;
    color: #212529;
    text-align: left;
    max-width: 320px;
}

.chip-body:focus {
    outline: none;
}

.chip-label {
    font-weight: 600;
}

.chip-toggle-indicator {
    color: #adb5bd;
    font-size: 0.95em;
}

.chip-active .chip-toggle-indicator {
    color: #1779ba;
}

.chip-value {
    color: #495057;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 220px;
}

.chip-empty {
    color: #868e96;
    font-style: italic;
    /* The italic placeholder overhangs its box on the right; since "Any" never needs
       truncation, let it render fully instead of clipping the final letter against the
       chip's rounded border. Truncation still applies to active values via .chip-value. */
    overflow: visible;
    max-width: none;
}

.chip-clear {
    display: inline-flex;
    align-items: center;
    padding: 0 10px;
    border: none;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
    background: transparent;
    color: #495057;
    cursor: pointer;
    font-size: 0.75rem;
}

.chip-clear:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #c92a2a;
}

.add-filter-dropdown :deep(.add-filter-toggle) {
    padding: 6px 10px;
    margin: 0;
    color: #1779ba;
    text-decoration: none;
}

.add-filter-dropdown :deep(.add-filter-toggle:hover) {
    text-decoration: underline;
}

.clear-all-btn {
    margin: 0;
    color: #c92a2a;
    text-decoration: none;
    font-weight: 600;
    border: 1px solid #e6b8b8;
    border-radius: 16px;
    padding: 5px 12px;
    background: #fff;
    white-space: nowrap;
}

.clear-all-btn:hover {
    color: #fff;
    background: #c92a2a;
    border-color: #c92a2a;
}
</style>

<!-- Unscoped so it can target the teleported popover content. -->
<style>
.chip-popover {
    max-width: none;
    /* 320 (chip-popover-body min) + 16 (popover-body padding) + 2 (popover border) */
    min-width: 338px;
}

.chip-popover .popover-body {
    padding: 6px 8px;
    /* 320 (chip-popover-body min) + 16 (padding, border-box) */
    min-width: 336px;
    /* Center the inline-block chip-popover-body when the popover is wider than
       its content (e.g. when a chip is narrower than POPOVER_MIN_WIDTH). */
    text-align: center;
}

.chip-popover .chip-popover-body {
    /* Reset alignment for editor content inside the centered wrapper. */
    text-align: left;
}

.chip-popover .chip-popover-body {
    /* Sized in JS to max(chipWidth, POPOVER_MIN_WIDTH) so editors that stretch to
       100% (v-select, multiselect, el-date-picker) get a usable width even when
       the chip itself is narrow (e.g. "Store Any"). Kept inline-block so the fixed
       width still resolves cleanly for percentage-width children. */
    display: inline-block;
}

.chip-popover .chip-popover-header-extra:not(:empty) {
    margin-bottom: 6px;
    font-size: 0.85rem;
}
</style>
